import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { IconEditorial, IconResearch, IconHumanAI, IconUpdates } from './icons/BlinkIcons';
export function WhyBlinkblis() {
  const {
    t
  } = useLanguage();
  const reducedMotion = useReducedMotion();
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px), (hover: none), (pointer: coarse)');
    const update = () => setIsCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const motionEnabled = !reducedMotion && !isCompact;
  return <section className="py-20 md:py-32 relative z-10 border-t border-white/5 bg-surface/20" data-id="element-337" data-mp="1u3e7jpy7tmz">
      <div className="max-w-7xl mx-auto px-5 sm:px-6" data-id="element-338" data-mp="1i4fjttbiysug">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-339" data-mp="1d7yremxalsbk">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-340" data-mp="2u90ojc1zd3xg">
            <h2 className="text-[42px] md:text-5xl lg:text-6xl font-display font-medium mb-6 md:mb-8 leading-[1.1]" data-id="element-341" data-mp="394q0ehdgc0og">
              {t('trust.title')}
            </h2>
            <p className="text-white/60 text-[18px] md:text-xl leading-relaxed font-light mb-10 md:mb-12 max-w-lg" data-id="element-342" data-mp="2gguabvie170n">
              {t('trust.subtitle')}
            </p>

            <div className="space-y-8" data-id="element-343" data-mp="2lelzihrnef15">
              {[{
              icon: IconEditorial,
              title: t('trust.pillars.editorial.title'),
              desc: t('trust.pillars.editorial.desc')
            }, {
              icon: IconResearch,
              title: t('trust.pillars.research.title'),
              desc: t('trust.pillars.research.desc')
            }, {
              icon: IconHumanAI,
              title: t('trust.pillars.humanai.title'),
              desc: t('trust.pillars.humanai.desc')
            }, {
              icon: IconUpdates,
              title: t('trust.pillars.updates.title'),
              desc: t('trust.pillars.updates.desc')
            }].map((item, i) => <div key={i} className="flex gap-4 md:gap-6 items-start group" data-id="element-344" data-mp="11tsfc58b5uft" data-mpi={i}>
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-white/5 flex items-center justify-center shrink-0 mt-1 md:group-hover:bg-accent/10 transition-colors duration-500 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-345" data-mp="1gqjfwhtpxc70">
                    <item.icon className="w-5 h-5 md:w-6 md:h-6 text-white/60 md:group-hover:text-accent transition-colors duration-500" data-id="element-346" data-mp="2s6rnwyrx5fq2" />
                  </div>
                  <div data-id="element-347" data-mp="2uo1q171kdxcw">
                    <h4 className="text-[18px] md:text-xl font-display font-medium mb-1 md:mb-2 text-white/90 md:group-hover:text-white transition-colors" data-id="element-348" data-mp="2uns01jgirq77">
                      {item.title}
                    </h4>
                    <p className="text-white/50 text-[15px] md:text-base font-light leading-relaxed" data-id="element-349" data-mp="2lqhmtsk55qzn">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative h-[380px] md:h-[500px] max-w-[360px] mx-auto md:max-w-none w-full rounded-[2.5rem] glass-panel border border-white/10 overflow-hidden flex items-center justify-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-id="element-350" data-mp="3idcky7w8gxj4">
            <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] rounded-[2.5rem] pointer-events-none" data-id="element-351" data-mp="28gpl469cvljm" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,106,0,0.15),transparent_50%)]" data-id="element-352" data-mp="23ixvjiw6obil" />

            {/* Abstract UI representation */}
            <motion.div className="w-[85%] h-[85%] border border-white/10 rounded-2xl bg-surface/40 backdrop-blur-2xl p-4 md:p-6 flex flex-col gap-4 md:gap-5 shadow-[0_24px_64px_rgba(0,0,0,0.6)] relative overflow-hidden" animate={!motionEnabled ? undefined : {
            y: [-4, 4, -4]
          }} transition={!motionEnabled ? undefined : {
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-353" data-mp="2w60dp0gjy9mo">
              <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] rounded-2xl pointer-events-none" data-id="element-354" data-mp="11ol3i5g4goif" />

              {/* Header */}
              <div className="flex items-center justify-between" data-id="element-355" data-mp="1ao7zuctzv44m">
                <div className="w-1/4 h-3 rounded-full bg-white/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-356" data-mp="1apj7wouh2y1j" />
                <div className="flex gap-2" data-id="element-357" data-mp="1flhz0k76qu5n">
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-358" data-mp="1kldu1txwnb92" />
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-359" data-mp="1pde1s8k7v8g7" />
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-360" data-mp="1pf8ljjjr4uip" />
                </div>
              </div>

              <div className="w-full h-px bg-white/5" data-id="element-361" data-mp="1tgk2z1sh29ft" />

              {/* Content */}
              <div className="flex-1 flex gap-5" data-id="element-362" data-mp="1ybppa1psdq4f">
                {/* Sidebar */}
                <div className="w-1/3 h-full flex flex-col gap-3" data-id="element-363" data-mp="23df3fq8b2jwq">
                  <div className="w-full h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-364" data-mp="39vxbiy2vj9s" />
                  <div className="w-3/4 h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-365" data-mp="3ui9saa7d2xb4" />
                  <div className="w-5/6 h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-366" data-mp="d1s19amui3lx" />
                </div>

                {/* Main Area */}
                <div className="flex-1 h-full flex flex-col gap-4" data-id="element-367" data-mp="m03vdmmx7jj4">
                  <div className="w-full h-32 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/20 shadow-[inset_0_1px_1px_rgba(255,106,0,0.2)] relative overflow-hidden" data-id="element-368" data-mp="hztqkdav6z5u">
                    {/* Abstract Chart Line */}
                    <motion.svg className="absolute bottom-0 left-0 w-full h-full text-accent/40" viewBox="0 0 100 100" preserveAspectRatio="none" animate={reducedMotion ? undefined : {
                    y: [-2, 2, -2]
                  }} transition={reducedMotion ? undefined : {
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1
                  }} data-id="element-369" data-mp="r0rnkbmvy6ex">
                      <path d="M0,100 L0,80 Q25,70 50,40 T100,20 L100,100 Z" fill="currentColor" opacity="0.2" data-id="element-370" data-mp="fl54oz33g0ss" />
                      <path d="M0,80 Q25,70 50,40 T100,20" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-371" data-mp="29yejn6eeseif" />
                    </motion.svg>
                  </div>
                  <div className="flex gap-4 flex-1" data-id="element-372" data-mp="xpt3yt5685mz">
                    <div className="flex-1 rounded-xl bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-373" data-mp="xq2uqiy1igtq" />
                    <div className="flex-1 rounded-xl bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-374" data-mp="swrlyc9o3pz6" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}